import type { FC } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAvaliableI2vModels } from 'src/components/GenerationTask/MultiColumnGenerationForm/ImageToVideo/helper'
import { cx } from 'src/utils/cx'
import { getI18nKeyForI2vModelName } from 'src/utils/task'

type Props = {
  model: string | undefined
} & StyleProps

/**
 * Display-only i2v model used in a t2i2v task.
 * Non-linkable, non-likeable. Uses dynamic i2v model config for image.
 */
const I2vModelUsedDisplay: FC<Props> = ({ model, className, style }) => {
  const { t } = useTranslation(['generation', 'common'])
  const { models } = useAvaliableI2vModels()

  const modelItem = useMemo(() => {
    if (!model) return undefined
    return models.find(it => it.model === model)
  }, [models, model])

  if (!model) return null

  const imageSrc = modelItem?.image ?? t('common:resources.miochan_sad')
  const title = t([
    `generation:image-to-video.parameters.model.${getI18nKeyForI2vModelName(model)}.label`,
    'common:unknown',
  ])

  return (
    <div
      className={cx(
        'flex items-center gap-2 p-1 -m-1 text-left rounded-xl',
        '[&:not(&:has([data-capture-hover]:hover))]:hover:bg-black/10',
        'dark:[&:not(&:has([data-capture-hover]:hover))]:hover:bg-white/10',
        className,
      )}
      style={style}
    >
      <div className="flex-none grid size-16 overflow-hidden bg-zinc-300 dark:bg-zinc-600 rounded-lg ring-1 ring-black/10 dark:ring-white/10">
        <img
          src={imageSrc}
          alt={title}
          className="row-start-1 col-start-1 object-cover w-full h-full"
        />
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="flex gap-1 items-center">
          <span className="rounded w-fit h-auto px-1.5 py-0 mr-0 bg-stone-300 text-black text-xs">
            I2V
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex-1 w-full truncate text-sm font-medium">
            {title}
          </span>
        </div>
      </div>
    </div>
  )
}

export default I2vModelUsedDisplay
